/**
 * Firestore paymentReceipt 또는 PortOne 카드 필드 → 결제방법 표시 (functions/portoneCardDisplay.ts 와 동일 규칙)
 */
import type { LicensePaymentReceipt } from '../types/license';

export type PortoneCardFields = {
  publisher?: string;
  issuer?: string;
  brand?: string;
  type?: string;
  name?: string;
};

const CARD_COMPANY_BY_CODE: Record<string, string> = {
  SHINHAN: '신한카드',
  SHINHAN_CARD: '신한카드',
  SHINHAN_BANK: '신한카드',
  KOOKMIN: 'KB국민카드',
  KOOKMIN_CARD: 'KB국민카드',
  KOOKMIN_BANK: 'KB국민카드',
  KB: 'KB국민카드',
  KB_CARD: 'KB국민카드',
  SAMSUNG: '삼성카드',
  SAMSUNG_CARD: '삼성카드',
  HYUNDAI: '현대카드',
  HYUNDAI_CARD: '현대카드',
  LOTTE: '롯데카드',
  LOTTE_CARD: '롯데카드',
  BC: 'BC카드',
  BC_CARD: 'BC카드',
  NH: 'NH농협카드',
  NH_CARD: 'NH농협카드',
  NH_NONGHYUP: 'NH농협카드',
  NH_NONGHYUP_BANK: 'NH농협카드',
  NONGHYUP: 'NH농협카드',
  HANA: '하나카드',
  HANA_CARD: '하나카드',
  HANA_BANK: '하나카드',
  WOORI: '우리카드',
  WOORI_CARD: '우리카드',
  WOORI_BANK: '우리카드',
  CITI: '씨티카드',
  CITI_CARD: '씨티카드',
  CITI_BANK: '씨티카드',
  SC: 'SC제일은행',
  SC_CARD: 'SC제일은행',
  SC_BANK: 'SC제일은행',
  SC_BANK_KOREA: 'SC제일은행',
  KAKAO: '카카오뱅크',
  KAKAO_BANK: '카카오뱅크',
  K_BANK: '케이뱅크',
  KBANK: '케이뱅크',
  TOSS: '토스뱅크',
  TOSS_BANK: '토스뱅크',
  SUHYUP: '수협카드',
  SUHYUP_BANK: '수협카드',
  JEONBUK: '전북카드',
  JEONBUK_BANK: '전북카드',
  GWANGJU: '광주카드',
  GWANGJU_BANK: '광주카드',
  BUSAN: '부산카드',
  BUSAN_BANK: '부산카드',
  DAEGU: '대구카드',
  DAEGU_BANK: '대구카드',
  KYONGNAM: '경남카드',
  KYONGNAM_BANK: '경남카드',
  JEJU: '제주카드',
  JEJU_BANK: '제주카드',
  POST: '우체국카드',
  EPOST: '우체국카드',
  UNIONPAY: '은련카드',
};

const CARD_BRAND_LABEL: Record<string, string> = {
  VISA: 'Visa',
  MASTER: 'Mastercard',
  UNIONPAY: 'UnionPay',
  JCB: 'JCB',
  AMEX: 'Amex',
  DINERS: 'Diners',
  LOCAL: '',
};

const CARD_TYPE_LABEL: Record<string, string> = {
  CREDIT: '신용',
  DEBIT: '체크',
  GIFT: '기프트',
};

function normCode(v: unknown): string {
  return String(v ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

function mapCardCompanyCode(code: string): string {
  const k = normCode(code);
  if (!k) return '';
  if (CARD_COMPANY_BY_CODE[k]) return CARD_COMPANY_BY_CODE[k];
  if (/[가-힣]/.test(code)) return code.trim();
  for (const [key, label] of Object.entries(CARD_COMPANY_BY_CODE)) {
    if (k.includes(key) || key.includes(k)) return label;
  }
  return '';
}

function pickCardCompanyName(card: PortoneCardFields): string {
  const fromName = typeof card.name === 'string' ? card.name.trim() : '';
  if (fromName && /[가-힣]/.test(fromName)) return fromName;
  const pub = mapCardCompanyCode(card.publisher || '');
  if (pub) return pub;
  const iss = mapCardCompanyCode(card.issuer || '');
  if (iss) return iss;
  if (fromName) return fromName;
  const raw = String(card.publisher || card.issuer || '').trim();
  if (raw && /[가-힣]/.test(raw)) return raw;
  return '';
}

export function formatPaymentMethodDisplay(card: PortoneCardFields | null | undefined): string {
  if (!card) return '카드';
  const company = pickCardCompanyName(card);
  const brandKey = normCode(card.brand);
  const brandLabel = brandKey && brandKey !== 'LOCAL' ? CARD_BRAND_LABEL[brandKey] || '' : '';
  const typeKey = normCode(card.type);
  const typeLabel = typeKey ? CARD_TYPE_LABEL[typeKey] || '' : '';

  const head = [company, brandLabel].filter(Boolean).join(' · ');
  if (!head) return typeLabel ? `카드 (${typeLabel})` : '카드';
  return typeLabel ? `${head} (${typeLabel})` : head;
}

/** 저장된 paymentReceipt 필드로 결제방법 문구 생성 (구매 건 호환) */
export function formatPaymentMethodFromReceipt(pr: LicensePaymentReceipt | null | undefined): string {
  if (!pr) return '카드';
  const stored = typeof pr.paymentMethodDisplay === 'string' ? pr.paymentMethodDisplay.trim() : '';
  if (stored) return stored;

  const card: PortoneCardFields = {
    publisher: pr.cardIssuer,
    issuer: pr.cardIssuerCode,
    brand: pr.cardBrand,
    type: pr.cardProductType,
    name: pr.cardProductName,
  };
  const formatted = formatPaymentMethodDisplay(card);
  if (formatted !== '카드') return formatted;

  const legacy = typeof pr.cardIssuer === 'string' ? pr.cardIssuer.trim() : '';
  if (legacy && legacy !== '카드') {
    const mapped = mapCardCompanyCode(legacy);
    return mapped || legacy;
  }
  return '카드';
}
